import React, { useState, useImperativeHandle, forwardRef } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Button,
  MenuItem,
  useTheme,
  useMediaQuery,
  Grid,
  List,
  ListItem,
  ListItemText,
  Collapse,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link } from 'react-router-dom';
import { ExpandMore } from '@mui/icons-material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import TerrainIcon from '@mui/icons-material/Terrain';
import ComputerIcon from '@mui/icons-material/Computer';
import BusinessIcon from '@mui/icons-material/Business';
import HandshakeIcon from '@mui/icons-material/Handshake';
import WorkIcon from '@mui/icons-material/Work';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import EmailIcon from '@mui/icons-material/Email';

const menuItems = [
  { text: 'Home', path: '/' },
  {
    text: 'About',
    path: '/about',
    subItems: [
      { text: 'Vision & Mission', path: '/about/vision-mission' },
      { text: 'Leadership & Staff', path: '/about/leadership' },
      { text: 'Social & Community', path: '/about/social-community' },
      //{ text: 'Innovation', path: '/about/innovation' },
      { text: 'Strategy', path: '/about/strategy' },
      { text: 'Partners & Affiliates', path: '/about/partners' },
      { text: 'Awards', path: '/about/awards' },
      { text: 'Corporate Responsibility', path: '/about/responsibility' },
      { text: 'Legal', path: '/about/legal' },
      { text: 'Careers', path: '/about/careers' },
    ],
  },
  {
    text: 'Solutions',
    path: '/solutions',
    subItems: [
      { text: 'Digital Aerial Photography & LiDAR', path: '/solutions/aerial-photography' },
      { text: 'Drone Mapping', path: '/solutions/drone-mapping' },
      { text: 'Satellite Imaging', path: '/solutions/satellite-imagery' },
      { text: 'Geodetic Survey', path: '/solutions/geodetic-survey' },
      { text: 'Hydrographic Survey', path: '/solutions/hydrographic-survey' },
      { text: 'Geophysical Survey', path: '/solutions/geophysical-survey' },
      { text: 'Cadastral Survey', path: '/solutions/cadastral-survey' },
      { text: 'GIS Solutions', path: '/solutions/gis-developments' },
      { text: 'Spatial Planning', path: '/solutions/cartography' },
      { text: 'Land Information Management', path: '/solutions/lims' },
      { text: 'Software Development', path: '/solutions/software-development' },
    ],
  },
  { text: 'News & Blogs', path: '/news' },
  { text: 'Contact', path: '/contact' },
];

const dropdownStyles = {
  paper: {
    mt: 0.5,
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    borderRadius: '8px',
    overflow: 'visible',
    position: 'absolute',
    width: '700px',
    p: 2,
    backgroundColor: 'white',
    zIndex: 1300,
    border: '1px solid #eaeaea',
    opacity: 1,
    transition: 'all 0.3s ease',
  },
  menuItem: {
    color: '#333',
    py: 1,
    px: 1.5,
    fontSize: '0.85rem',
    borderRadius: '4px',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(0,64,64,0.08)',
      color: '#004040',
      transform: 'translateX(2px)',
    },
  },
  categoryTitle: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#004040',
    mb: 1,
    mt: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  categoryIcon: {
    fontSize: '1.2rem',
    color: '#004040',
  },
  categorySection: {
    mb: 2,
    p: 1.5,
    borderRadius: '6px',
    backgroundColor: '#f9f9f9',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#f0f7f7',
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
      transform: 'translateY(-2px)',
    },
  },
  dropdownTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#004040',
    mb: 2,
    pb: 1,
    borderBottom: '1px solid #eaeaea',
  },
  menuWrapper: {
    position: 'relative',
  }
};

const Navbar = forwardRef((props, ref) => {
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const [subMenuAnchors, setSubMenuAnchors] = useState({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  let timeoutId = null; // To manage smooth transitions

  const handleMouseEnter = (event, index) => {
    clearTimeout(timeoutId); // Clear any close delay
    
    // If another menu is open, close it first
    if (openMenuIndex !== null && openMenuIndex !== index) {
      setOpenMenuIndex(null);
      // Small delay to allow React to process the close before opening new menu
      setTimeout(() => {
        setAnchorEl(event.currentTarget);
        setOpenMenuIndex(index);
      }, 10);
    } else {
      // Otherwise just open the current menu
      setAnchorEl(event.currentTarget);
      setOpenMenuIndex(index);
    }
  };

  const handleMouseLeave = () => {
    timeoutId = setTimeout(() => {
      setAnchorEl(null);
      setOpenMenuIndex(null);
    }, 300); // Increased delay for smoother transitions
  };

  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleSubMenuOpen = (event, index) => {
    setSubMenuAnchors({ ...subMenuAnchors, [index]: event.currentTarget });
  };

  const handleSubMenuClose = (index) => {
    setSubMenuAnchors({ ...subMenuAnchors, [index]: null });
  };

  const handleExternalSolutionsClick = (event) => {
    // Find the Solutions menu item index
    const solutionsIndex = menuItems.findIndex(item => item.text === 'Solutions');
    if (solutionsIndex !== -1) {
      setAnchorEl(event.currentTarget);
      setOpenMenuIndex(solutionsIndex);
    }
  };

  useImperativeHandle(ref, () => ({
    openSolutionsMenu: handleExternalSolutionsClick
  }), []);

  const renderMenuContent = (menuType) => {
    const solutionsCategories = [
      {
        title: 'Aerial Services',
        icon: <CameraAltIcon sx={dropdownStyles.categoryIcon} />,
        items: [
          { text: 'Aerial & LiDAR Mapping', path: '/solutions/aerial-photography' },
          { text: 'Drone Mapping', path: '/solutions/drone-mapping' },
          { text: 'Satellite Imaging', path: '/solutions/satellite-imagery' },
        ],
      },
      {
        title: 'Geospatial Services',
        icon: <TerrainIcon sx={dropdownStyles.categoryIcon} />,
        items: [
          { text: 'Geodetic Survey', path: '/solutions/geodetic-survey' },
          { text: 'Hydrographic Survey', path: '/solutions/hydrographic-survey' },
          { text: 'Geophysical Survey', path: '/solutions/geophysical-survey' },
          { text: 'Cadastral Survey', path: '/solutions/cadastral-survey' },
        ],
      },
      {
        title: 'GIS & Software',
        icon: <ComputerIcon sx={dropdownStyles.categoryIcon} />,
        items: [
          { text: 'GIS Solutions', path: '/solutions/gis-developments' },
          { text: 'Spatial Planning', path: '/solutions/cartography' },
          { text: 'Land Information Management', path: '/solutions/lims' },
          { text: 'Software Development', path: '/solutions/software-development' },
        ],
      },
    ];

    const aboutCategories = [
      {
        title: 'Company',
        icon: <BusinessIcon sx={dropdownStyles.categoryIcon} />,
        items: [
          { text: 'Vision & Mission', path: '/about/vision-mission' },
          { text: 'Leadership & Staff', path: '/about/leadership' },
          { text: 'Social & Community', path: '/about/social-community' },
          //{ text: 'Innovation', path: '/about/innovation' },
          { text: 'Strategy', path: '/about/strategy' },
        ],
      },
      {
        title: 'Partnerships',
        icon: <HandshakeIcon sx={dropdownStyles.categoryIcon} />,
        items: [
          { text: 'Partners & Affiliates', path: '/about/partners' },
          { text: 'Awards', path: '/about/awards' },
        ],
      },
      {
        title: 'Corporate',
        icon: <WorkIcon sx={dropdownStyles.categoryIcon} />,
        items: [
          { text: 'Corporate Responsibility', path: '/about/responsibility' },
          { text: 'Legal', path: '/about/legal' },
          { text: 'Careers', path: '/about/careers' },
        ],
      },
    ];

    const categories = menuType === 'Solutions' ? solutionsCategories : aboutCategories;

  return (
      <Box sx={{ width: '100%' }}>
        <Typography sx={dropdownStyles.dropdownTitle}>
          {menuType === 'Solutions' ? 'Our Solutions' : 'About Us'}
        </Typography>
        <Grid container spacing={2}>
          {categories.map((category) => (
            <Grid item xs={4} key={category.title}>
              <Box sx={dropdownStyles.categorySection}>
                <Typography sx={dropdownStyles.categoryTitle}>
                  {category.icon}
                  {category.title}
                </Typography>
    <Box>
                  {category.items.map((item) => (
                    <MenuItem
                      key={item.text}
                      component={Link}
                      to={item.path}
                      onClick={() => setOpenMenuIndex(null)}
                      sx={dropdownStyles.menuItem}
                    >
                      {item.text}
                    </MenuItem>
                  ))}
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  const renderMobileSubMenu = (item, index) => {
    const isOpen = Boolean(subMenuAnchors[index]);
    const items = item.subItems;

    return (
      <Box key={item.text}>
        <MenuItem
          onClick={(e) => handleSubMenuOpen(e, index)}
        sx={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 2,
          }}
        >
          <Typography variant="body1">{item.text}</Typography>
          <ExpandMore 
            sx={{ 
              transform: isOpen ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s',
              ml: 1 
            }} 
          />
        </MenuItem>
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {items.map((subItem) => (
              <ListItem
                key={subItem.text}
                component={Link}
                to={subItem.path}
                onClick={handleMobileMenuClose}
              sx={{
                  pl: 4,
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: 'primary.lighter',
                    color: 'primary.main',
                  },
                }}
              >
                <ListItemText 
                  primary={subItem.text}
                  primaryTypographyProps={{
                    variant: 'body2',
                    sx: { fontSize: '0.9rem' }
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box sx={{ 
      width: '100%', 
      position: 'fixed',
      top: 0,
      zIndex: 1100,
      bgcolor: 'white'
    }}>
      <Box sx={{ height: '8px', bgcolor: 'white' }} />
      <AppBar 
        position="fixed" 
      elevation={1}
        sx={{ 
        top: 0,
          bgcolor: 'white',
        height: 140,
        mt: 1,
        }}
      >
        <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ height: 140 }}>
            {/* Logo */}
          <Box>
            <img src="/images/newlogo.png" alt="Geomaps Logo" height="140" width={260} />
            </Box>

            <Box sx={{ display: { xs: 'flex', md: 'none' }, ml: 'auto' }}>
              <IconButton
                size="large"
                onClick={handleMobileMenuOpen}
                sx={{ color: '#008080' }}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={mobileMenuAnchor}
                open={Boolean(mobileMenuAnchor)}
                onClose={handleMobileMenuClose}
                PaperProps={{
                  sx: {
                    maxHeight: '80vh',
                    width: '100%',
                    maxWidth: '300px',
                    mt: 1,
                    bgcolor: 'white',
                  }
                }}
              >
                {menuItems.map((item, index) => (
                  item.subItems ? (
                    renderMobileSubMenu(item, index)
                  ) : (
                    <MenuItem
                      key={item.text}
                      component={Link}
                      to={item.path}
                      onClick={handleMobileMenuClose}
                      sx={{
                        py: 2,
                        color: '#008080',
                        '&:hover': {
                          backgroundColor: 'rgba(0,128,128,0.1)',
                        },
                      }}
                    >
                      <Typography variant="body1">{item.text}</Typography>
                    </MenuItem>
                  )
                ))}
              </Menu>
            </Box>

          {/* Desktop Navigation Section with Slogan */}
          <Box 
            sx={{ 
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              alignItems: 'flex-end',
              ml: 'auto',
              height: 140,
              justifyContent: 'space-between',
              py: 1.5,
            }}
          >
            {/* Slogan */}
            <Box
              component="img"
              src="/images/theme2.png" 
              alt="GeoMaps Africa Logo"
              sx={{
                width: '400px',
                height: 'auto',
                display: 'block',
                marginLeft: 'auto',
                mt: 6, // Added margin top to move slogan down
              }}
            />

            <Box sx={{ 
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 1,
            }}>
              {/* Social Media Icons */}
              <Box sx={{ 
                display: 'flex',
                gap: 1,
                mr: 20,
              }}>
                <IconButton 
                  href="https://www.facebook.com/profile.php?id=61575115388714" 
                  target="_blank"
                  sx={{ 
                    color: '#1877F2', // Facebook blue
                    '&:hover': { transform: 'translateY(-2px)' }
                  }}
                >
                  <FacebookIcon />
                </IconButton>
                <IconButton 
                  href="https://x.com/geomapsafrica" 
                  target="_blank"
                  sx={{ 
                    color: '#1DA1F2', // Twitter blue
                    '&:hover': { transform: 'translateY(-2px)' }
                  }}
                >
                  <TwitterIcon />
                </IconButton>
                <IconButton 
                  href="https://www.linkedin.com/company/geomaps-africa/" 
                  target="_blank"
                  sx={{ 
                    color: '#0077B5', // LinkedIn blue
                    '&:hover': { transform: 'translateY(-2px)' }
                  }}
                >
                  <LinkedInIcon />
                </IconButton>
                <IconButton 
                  href="https://www.instagram.com/geomaps_africa/" 
                  target="_blank"
                  sx={{ 
                    color: '#E4405F', // Instagram pink
                    '&:hover': { transform: 'translateY(-2px)' }
                  }}
                >
                  <InstagramIcon />
                </IconButton>
                <IconButton 
                  href="https://webmail.geoafrica.com" 
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    color: '#1A73E8', // Email blue (like Gmail)
                    '&:hover': { transform: 'translateY(-2px)' }
                  }}
                >
                  <EmailIcon />
                </IconButton>               
              </Box>

              {/* Navigation Links */}
              <Box sx={{ 
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 0.75,
              }}>
                {menuItems.map((item, index) => (
                  <Box 
                    key={item.text}
                    sx={{ 
                      position: 'relative',
                    }}
                    onMouseEnter={(e) => item.subItems && handleMouseEnter(e, index)}
                    onMouseLeave={item.subItems && handleMouseLeave}
                  >
                    {item.subItems ? (
                    <>
                      <Button
                          component={Link}
                          to={item.path}
                        sx={{
                            color: 'white',
                            bgcolor: '#004040',
                            px: 1.5,
                            py: 0,
                            fontSize: '0.75rem',
                            textTransform: 'none',
                            borderRadius: '4px',
                            height: 25,
                            minWidth: 'auto',
                          '&:hover': {
                              bgcolor: '#002020',
                          },
                        }}
                      >
                          {item.text}
                          <ExpandMore sx={{ ml: 0.3, fontSize: '0.75rem' }} />
                      </Button>
                        <Menu
                          anchorEl={openMenuIndex === index ? anchorEl : null}
                          open={openMenuIndex === index}
                          onClose={() => setOpenMenuIndex(null)}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                          }}
                          keepMounted
                          disablePortal
                          MenuListProps={{
                            onMouseEnter: () => clearTimeout(timeoutId),
                            onMouseLeave: handleMouseLeave,
                            sx: { 
                              p: 0,
                              overflow: 'visible'
                            }
                          }}
                          PaperProps={{
                            sx: {
                              ...dropdownStyles.paper,
                              overflow: 'visible',
                              mt: 1,
                              '& .MuiList-root': {
                                padding: 0,
                                overflow: 'visible'
                              }
                            },
                            onMouseEnter: () => clearTimeout(timeoutId),
                            onMouseLeave: handleMouseLeave
                          }}
                          sx={{
                            '& .MuiMenu-paper': {
                              overflow: 'visible'
                            }
                          }}
                          TransitionProps={{
                            timeout: 1
                          }}
                        >
                          {(item.text === 'Solutions' || item.text === 'About') ? (
                            renderMenuContent(item.text)
                          ) : (
                            item.subItems.map((subItem) => (
                            <MenuItem
                                key={subItem.text}
                              component={Link}
                                to={subItem.path}
                                onClick={() => setOpenMenuIndex(null)}
                              sx={dropdownStyles.menuItem}
                            >
                                {subItem.text}
                            </MenuItem>
                            ))
                          )}
                        </Menu>
                    </>
                  ) : (
                    <Button
                      component={Link}
                      to={item.path}
                      sx={{
                          color: 'white',
                          bgcolor: '#004040',
                          px: 1.5,
                          py: 0,
                          fontSize: '0.75rem',
                          textTransform: 'none',
                          borderRadius: '4px',
                          height: 25,
                          minWidth: 'auto',
                        '&:hover': {
                            bgcolor: '#002020',
                        },
                      }}
                    >
                        {item.text}
                    </Button>
                  )}
                </Box>
              ))}
              </Box>
            </Box>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
    </Box>
  );
});

export default Navbar; 